import { toFixed } from './tools';
import Big, { RoundingMode } from 'big.js';

// 舍入规则 - Big.js的舍入模式
export const ROUND_DOWN = 0 as RoundingMode; // 向零舍入
Big.DP = 2; // 默认小数位数
Big.RM = ROUND_DOWN; // 默认舍入模式（向零舍入）

const formatRoundNum = (num: number, pre: number) => (Math.floor(num * Math.pow(10, pre)) / Math.pow(10, pre)).toFixed(pre);
interface OptionsType {
  /** 简写 */
  shortening?: boolean;
  /**  */
  /** 小额金额保留位数 */
  smallUnitFixed?: number;
  /** 是否统一百万以上才缩写 */
  useMillion?: boolean;
  /** 是否用长数字 */
  useLongNum?: boolean;
}
const DEFAULT_OPTIONS: OptionsType = {
  shortening: false,
  useMillion: false,
  useLongNum: false
};
// 印度 卢比
const INRCurrencyFormat = (money: number | string, fixed = 0, options: OptionsType) => {
  const total = Number(money);
  const croreStr = options.shortening ? 'Cr' : 'Crore';
  const LakhStr = options.shortening ? 'L' : 'Lakh';
  let changed;
  if (total >= 10000000) {
    // 大于千万用crores
    changed = total / 10000000;
    return `${formatRoundNum(changed, fixed)} ${croreStr}${changed > 1 && !options.shortening ? 's' : ''}`;
  }
  if (total >= 100000 && !options?.useMillion) {
    // 大于十万用lakhs
    changed = total / 100000;
    return `${formatRoundNum(changed, fixed)} ${LakhStr}${changed > 1 && !options.shortening ? 's' : ''}`;
  }
  if (total >= 1000000 && options?.useMillion && !options?.useLongNum) {
    // 大于百万用lakhs
    changed = total / 100000;
    return `${formatRoundNum(changed, fixed)} ${LakhStr}${changed > 1 && !options.shortening ? 's' : ''}`;
  }
  if ('smallUnitFixed' in options) {
    if (options.smallUnitFixed === 0) return Math.floor(total).toLocaleString();
    return toFixed(total, options.smallUnitFixed || 0).toLocaleString();
  }
  return total.toLocaleString();
};
// 巴西雷亚尔
const BLRCurrencyFormat = (money: number | string, fixed = 0, options: OptionsType) => {
  const total = Number(money);
  const millionStr = options.shortening ? 'M' : 'Milhão'; //  百万
  const billionStr = options.shortening ? 'B' : 'Bilhão'; // 十亿
  const tenThousandStr = options.shortening ? 'T' : 'Trilhão'; // 万亿
  let changed;
  if (total >= 1000000000000) {
    // 大于万亿
    changed = total / 1000000000000;
    return `${formatRoundNum(changed, fixed)} ${tenThousandStr}`;
  }
  if (total >= 1000000000) {
    // 大于十亿
    changed = total / 1000000000;
    return `${formatRoundNum(changed, fixed)} ${billionStr}}`;
  }
  if (total >= 1000000) {
    // 大于百万
    changed = total / 1000000;
    return `${formatRoundNum(changed, fixed)} ${millionStr}}`;
  }
  if ('smallUnitFixed' in options) {
    if (options.smallUnitFixed === 0) return Math.floor(total).toLocaleString();
    return toFixed(total, options.smallUnitFixed || 0).toLocaleString();
  }
  return total.toLocaleString();
};
// 泰达币 usdt
const USDTCurrencyFormat = (money: number | string, fixed = 0, options: OptionsType) => {
  const total = Number(money);
  const millionStr = 'M'; //  百万
  const billionStr = 'B'; // 十亿
  const tenThousandStr = 'T'; // 万亿
  let changed;
  if (total >= 1000000000000) {
    // 大于万亿
    changed = total / 1000000000000;
    return `${formatRoundNum(changed, fixed)} ${tenThousandStr}`;
  }
  if (total >= 1000000000) {
    // 大于十亿
    changed = total / 1000000000;
    return `${formatRoundNum(changed, fixed)} ${billionStr}}`;
  }
  if (total >= 1000000) {
    // 大于百万
    changed = total / 1000000;
    return `${formatRoundNum(changed, fixed)} ${millionStr}}`;
  }
  if ('smallUnitFixed' in options) {
    if (options.smallUnitFixed === 0) return Math.floor(total).toLocaleString();
    return toFixed(total, options.smallUnitFixed || 0).toLocaleString();
  }
  return total.toLocaleString();
};

const currencyConfig = {
  INR: {
    symbol: '₹',
    format: INRCurrencyFormat, // 印度货币展示比较特殊
  },
  JPY: {
    symbol: '¥',
    format: (money: number | string) => money.toLocaleString(),
  },
  BRL: {
    symbol: 'R$',
    format: BLRCurrencyFormat,
  },
  USDT: {
    symbol: '₮',
    format: USDTCurrencyFormat,
  },
};

const formatCurrency = (money: number | string, currencyCode: string, fixed = 0, options: OptionsType = DEFAULT_OPTIONS) => {
  const curConfig =
    currencyConfig[currencyCode.toUpperCase()] || currencyConfig['INR'];

  return curConfig.symbol + curConfig.format(money, fixed, options);
};


export function getCurrency(code: string) {
  return currencyConfig[code]?.symbol ?? '';
}

export function getCurrencyFmt(code: string, num: string | number = '') {
  const symbol = currencyConfig[code]?.symbol ?? '';
  return symbol + num;
}

/**
 * 截断小数，不进行四舍五入
 * @param value 数值
 * @param decimals 保留的小数位数（默认 2）
 * @returns 截断后的数值
 * 
 * @example
 * truncateDecimals(123.456, 2)  // 123.45
 * truncateDecimals(123.999, 2)  // 123.99 (不会变成 124.00)
 * truncateDecimals(123.4, 2)    // 123.40
 */
export function toNumFixed(value: number, decimals: number = 2) {
  // 使用 Big.js 精确截断，避免二进制浮点误差的问题，如2.28 -> 2.27 问题
  const b = new Big(value);
  const truncated = b.round(decimals, ROUND_DOWN);
  // 去掉多余的0和点，如12.00->12，12.10->12.1，12.01->12.01
  return truncated.toFixed(decimals).replace(/\.?0+$/, '');
}

export { formatCurrency };
